import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Input, InputNumber, Modal, Select } from 'antd';

import { introduceDifficulty, selectors, Blueprint } from 'state';

type Props = {
  blueprint: Blueprint;
  versionId: number;
  visible: boolean;
  hide: () => void;
};

/**
 * Introduce a new difficulty into an unlocked version, seeded from an existing
 * (sibling) difficulty's template. The new variant is created hidden.
 */
const IntroduceDifficultyModal: React.FC<Props> = ({
  blueprint,
  versionId,
  visible,
  hide,
}) => {
  const dispatch = useDispatch();
  const waiting = useSelector(selectors.blueprintsWaiting);
  const [form] = Form.useForm();

  const version = blueprint.versions.find(v => v.id === versionId);
  // Only difficulties that already have a variant on THIS version can seed.
  const seedableDifficultyIds = new Set(
    (version?.variants ?? []).map(variant => variant.difficulty.id),
  );
  const seedOptions = blueprint.difficulties.filter(d =>
    seedableDifficultyIds.has(d.id),
  );

  const nextLevel =
    blueprint.difficulties.reduce((max, d) => Math.max(max, d.level), 0) + 1;

  return (
    <Modal
      title="Introduce a difficulty"
      open={visible}
      onCancel={hide}
      footer={null}
      width={480}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          level: nextLevel,
          label: '',
          seed_from_difficulty_id: seedOptions[0]?.id,
        }}
        onFinish={() => {
          const data = form.getFieldsValue() as {
            level: number;
            label: string;
            seed_from_difficulty_id: number;
          };
          dispatch(
            introduceDifficulty({
              id: blueprint.id,
              level: data.level,
              label: data.label,
              seed_from_difficulty_id: data.seed_from_difficulty_id,
              on_version_id: versionId,
            }),
          );
          hide();
        }}
      >
        <Form.Item
          label="Level"
          name="level"
          rules={[{ required: true, message: 'Level is required' }]}
        >
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="Label"
          name="label"
          rules={[{ required: true, message: 'Label is required' }]}
        >
          <Input placeholder="e.g. Hard" />
        </Form.Item>
        <Form.Item
          label="Seed config from"
          name="seed_from_difficulty_id"
          rules={[
            { required: true, message: 'Pick a difficulty to seed from' },
          ]}
        >
          <Select placeholder="Existing difficulty">
            {seedOptions.map(d => (
              <Select.Option key={d.id} value={d.id}>
                {d.label} (level {d.level})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button onClick={hide} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={waiting}>
            Introduce
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default IntroduceDifficultyModal;
